// Dependencies
import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  
  useMediaQuery,
  useTheme,
  Chip,
  Card,
  CardContent,
  Stack,
  
  Fab,
  Collapse,
  IconButton,
} from "@mui/material";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import { GENERAL } from "../general/General";
import { LottieAnimation } from "../lottie/LottieAnimation.js";
import { useUser } from "../authMethods/UserContext";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import SaveIcon from "@mui/icons-material/Save";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ShareIcon from "@mui/icons-material/Share";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PeopleIcon from "@mui/icons-material/People";
import Swal from "sweetalert2";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import Profile from "./Profile.js";
import { Share } from "./Share";

const TeamBuilder = ({ updateBackground }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  //const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { userData } = useUser();
  
  // State variables
  const [pool, setPool] = useState([]);
  const [whiteTeam, setWhiteTeam] = useState([]);
  const [blackTeam, setBlackTeam] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [savedTeams, setSavedTeams] = useState([]);
  const [selectedTeamIndex, setSelectedTeamIndex] = useState("");
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [customTeamName, setCustomTeamName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [friendUrl, setFriendUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const [savedTeamsExpanded, setSavedTeamsExpanded] = useState(false);
  
  const isTeamEmpty = whiteTeam.length === 0 && blackTeam.length === 0;

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    updateBackground({
      image: GENERAL.BACKGROUND,
    });
    
    const handleWebSocketEvent = (event) => {
      if (
        event.type === "message" &&
        event.message.messageType === "favorites_data"
      ) {
        setFavorites(event.message.favoriteStatistics || []);
        setLoading(false);

        const selfPlayer = {
          id: userData?.userId || userData?.id || "",
          url: userData?.url || "",
          userStatistics: {
            userName: userData?.userName || userData?.name || "",
            name: userData?.name || "",
          },
          online: true,
        };

        const favoritesList = event.message.favoriteStatistics || [];
        const alreadyInFavorites = favoritesList.some(
          (user) => user?.userName === selfPlayer?.userStatistics?.userName
        );

        const initialPool = alreadyInFavorites
          ? favoritesList
          : [selfPlayer, ...favoritesList];

        setPool(initialPool);
        
        if(initialPool.length === 1){
          Swal.fire({
            title: t("Your friends list is empty—invite someone to get started!"),
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("Invite"),
            text: t("Invite your friends to join your team and play together!"),
            cancelButtonText: t("Cancel"),
          }).then((result) => {
            if (result.isConfirmed) {
              inviteToJoin(); 
            } else {
              goBack();
            }
          });
        }
      } else if (
        event.type === "message" &&
        event.message.messageType === "team_data_saved"
      ) {
        const savedTeam = event.message.team;
        setSavedTeams((prev) => [...prev, savedTeam]);

        Swal.fire({
          icon: "success",
          title: t("Team saved successfully!"),
          timer: 2500,
          showConfirmButton: false,
        });

        setIsSaving(false);
        setCustomTeamName("");
        clearTeams();
      } else if (
        event.type === "message" &&
        event.message.messageType === "team_error"
      ) {
        Swal.fire({
          icon: "error",
          title: t("Something went wrong. Please try again..."),
          timer: 2500,
          showConfirmButton: false,
        });
      } else if (
        event.type === "message" &&
        event.message.messageType === "get_saved_teams_response"
      ) {
        const teams = (event.message.teams || []).slice().reverse();
        setSavedTeams(teams);
      }
    };

    addListener(handleWebSocketEvent);
    requestForFavorites();
    requestForSavedTeams();

    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, []);

  const handleAvatarClick = (userStatistics) => {
    if(userStatistics?.id === userData?.id){
      setSelectedAvatar(userData);
    } else {
      setSelectedAvatar(userStatistics);
    }
    setProfileOpen(true);
  };

  const handleCloseProfile = () => {
    setProfileOpen(false);
    setSelectedAvatar(null);
  };

  function requestForFavorites() {
    const generalMessage = {
      messageType: "get_favorites",
    };
    sendMessage(generalMessage);
  }

  function requestForSavedTeams() {
    const generalMessage = {
      messageType: "get_saved_teams",
      userId: userData?.userId,
    };
    sendMessage(generalMessage);
  }

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const lists = {
      pool: [...pool],
      white: [...whiteTeam],
      black: [...blackTeam],
    };

    const [moved] = lists[source.droppableId].splice(source.index, 1);
    lists[destination.droppableId].splice(destination.index, 0, moved);

    setPool(lists.pool);
    setWhiteTeam(lists.white);
    setBlackTeam(lists.black);
  };

  const highlightName = (name) => {
    const index = name?.toLowerCase().indexOf(searchTerm.toLowerCase());
    if (index === -1 || !searchTerm) return name;

    return (
      <>
        {name?.substring(0, index)}
        <span style={{ backgroundColor: "#ffeb3b", padding: "0 2px", borderRadius: "2px" }}>
          {name?.substring(index, index + searchTerm.length)}
        </span>
        {name?.substring(index + searchTerm.length)}
      </>
    );
  };

  const filteredPool = pool.filter((user) => {
    const name = user?.userStatistics?.name || user?.name || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const clearTeams = () => {
    setPool([...pool, ...whiteTeam, ...blackTeam]);
    setWhiteTeam([]);
    setBlackTeam([]);
  };

  const isSameTeam = (team1, team2) => {
    const whiteIds1 = team1.whiteTeam.map(u => u.id).sort();
    const whiteIds2 = team2.whiteTeam.map(u => u.id).sort();
    const blackIds1 = team1.blackTeam.map(u => u.id).sort();
    const blackIds2 = team2.blackTeam.map(u => u.id).sort();
  
    return (
      JSON.stringify(whiteIds1) === JSON.stringify(whiteIds2) &&
      JSON.stringify(blackIds1) === JSON.stringify(blackIds2)
    );
  };

  const saveTeam = () => {
    if (!customTeamName.trim()) {
      Swal.fire({
        title: t("Please provide a team name"),
        text: t("You must provide a name for the team before saving"),
        icon: "warning",
        draggable: true,
        confirmButtonText: t("OK"),
      });
      return;
    }

    if (whiteTeam.length === 0 || blackTeam.length === 0) {
      Swal.fire({
        title: t("Missing Team Members"),
        text: t("You must add players to the team before saving"),
        icon: "warning",
        draggable: true,
        confirmButtonText: t("OK"),
      });
      return;
    }

    const newTeamData = {
      whiteTeam,
      blackTeam,
    };

    const existingTeam = savedTeams.find(team => isSameTeam(team, newTeamData));

    if (existingTeam) {
      Swal.fire({
        title: t("Duplicate Team Detected!"),
        text: `${t("You already have a saved team named")} "${existingTeam.name}".`,
        icon: "info",
        confirmButtonText: t("OK"),
      });
      return;
    }

    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "team_data_save";
    generalMessage.team.name = customTeamName;

    generalMessage.team.whiteTeam = whiteTeam.map((user) => ({
      id: user?.id || user?.userId || "",
      url: user?.url || "",
      userStatistics: {
        userName: user?.userStatistics?.userName || user?.userName || "",
        name: user?.userStatistics?.name || user?.name || "",
      },
      online: user?.online ?? false,
    }));

    generalMessage.team.blackTeam = blackTeam.map((user) => ({
      id: user?.id || user?.userId || "",
      url: user?.url || "",
      userStatistics: {
        userName: user?.userStatistics?.userName || user?.userName || "",
        name: user?.userStatistics?.name || user?.name || "",
      },
      online: user?.online ?? false,
    }));

    generalMessage.userId = userData?.userId;
    sendMessage(generalMessage);
    setIsSaving(true);

    setTimeout(() => {
      requestForFavorites();
    }, 1000);
  };

  const handleLoadTeam = (index) => {
    if (index === "") return;

    const selected = savedTeams[index];
    if (!selected) return;

    const allUsers = [
      ...favorites,
      {
        id: userData?.id || "",
        url: userData?.url || "",
        userStatistics: {
          userName: userData?.userName || userData?.name || "",
          name: userData?.name || "",
        },
        online: true,
      },
    ];

    const selectedUserIds = [
      ...selected.whiteTeam.map((u) => u.id),
      ...selected.blackTeam.map((u) => u.id),
    ];

    const poolUsers = allUsers.filter(
      (user) => !selectedUserIds.includes(user.id)
    );

    setPool(poolUsers);
    setWhiteTeam(selected.whiteTeam);
    setBlackTeam(selected.blackTeam);
    setSelectedTeamIndex(index);
  };

  const deleteTeam = (indexToDelete, idToDelete) => {
    const updated = savedTeams.filter((_, idx) => idx !== Number(indexToDelete));
    setSavedTeams(updated);

    if (selectedTeamIndex === indexToDelete) {
      setSelectedTeamIndex("");
    }
    
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "delete_team";
    generalMessage.team.id = idToDelete;
    sendMessage(generalMessage);
  };

  const handleDeleteTeam = (index) => {
    Swal.fire({
      title: t("Are you sure?"),
      text: t("This team will be deleted permanently!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("Yes, delete it!"),
      cancelButtonText: t("Cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        const selected = savedTeams[index];
        deleteTeam(index, selected?.id);
      }
    });
  };

  const handlePlayTeam = (index) => {
    const selected = savedTeams[index];
    if (!selected) return;

    setWhiteTeam(selected.whiteTeam);
    setBlackTeam(selected.blackTeam);

    navigate("/team-play", { state: { team: selected } });
  };

  const inviteToJoin = () => {
    let id = userData?.id;
    let gameURL = GENERAL.HOST + "friend/" + id;
    setFriendUrl(gameURL);

    setTimeout(() => {
      setOpen(true);
    }, 50);
  };

  const goBack = () => {
    navigate(-1);
  };

  // Minimal drag item component
  const MinimalDragItem = ({ user, index, isDragging, onClick }) => (
    <Chip
      label={highlightName(user.userStatistics?.name || user.name)}
      onClick={() => onClick(user)}
      variant="outlined"
      size={isMobile ? "small" : "medium"}
      sx={{
        mb: 0.5,
        mr: 0.5,
        cursor: "grab",
        backgroundColor: isDragging ? "#e3f2fd" : "white",
        border: "1px solid #4a707d",
        color: "#4a707d",
        fontWeight: "bold",
        fontSize: isMobile ? "0.75rem" : "0.875rem",
        height: isMobile ? "28px" : "32px",
        "& .MuiChip-label": {
          px: isMobile ? 1 : 1.5,
        },
        "&:hover": {
          backgroundColor: "#f5f5f5",
        },
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 2,
          right: 2,
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: user.online ? "#4caf50" : "#f44336",
          border: "1px solid white",
        },
      }}
    />
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <LottieAnimation
          path="https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/search.json"
          width={isMobile ? "200px" : "300px"}
          height={isMobile ? "200px" : "300px"}
          autoplay
          loop
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 2,
        maxWidth: "100%",
        mx: "auto",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <Profile
        open={profileOpen}
        onClose={handleCloseProfile}
        avatar={selectedAvatar}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {/* <DialogTitle>{t("Invite Your Friend")}</DialogTitle> */}
        <DialogContent>
          <Share currentUrl={friendUrl} />
        </DialogContent>
      </Dialog>

      {/* Header */}
      <Card sx={{ mb: 2, backgroundColor: "#4a707d", color: "white" }}>
        <CardContent sx={{ pb: "16px !important" }}>
          <Typography variant={isMobile ? "h6" : "h5"} align="center" fontWeight="bold">
            {t("Build Your Team")}
          </Typography>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} justifyContent="space-between" mb={2}>
        <Fab
          size={isMobile ? "small" : "medium"}
          color="primary"
          onClick={goBack}
          sx={{ backgroundColor: "#4a707d" }}
        >
          <ArrowBackIcon />
        </Fab>
        
        <Fab
          size={isMobile ? "small" : "medium"}
          color="secondary"
          onClick={inviteToJoin}
          sx={{ backgroundColor: "#2196f3" }}
        >
          <ShareIcon />
        </Fab>
      </Stack>

      {/* Search */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: "16px !important" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SearchIcon color="action" />
            <input
              type="text"
              placeholder={t("Search by name...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                borderRadius: "20px",
                border: "1px solid #ddd",
                outline: "none",
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <DragDropContext onDragEnd={onDragEnd}>
        {/* Available Players Pool */}
        <Card sx={{ mb: 2, backgroundColor: "#e8f5e8" }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="bold" color="#4a707d" mb={1}>
              <PeopleIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              {t("Available Players")} ({filteredPool.length})
            </Typography>
            <Droppable droppableId="pool">
              {(provided) => (
                <Box
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    minHeight: 60,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    p: 1,
                    backgroundColor: "white",
                    borderRadius: 2,
                    border: "2px dashed #4a707d",
                  }}
                >
                  {filteredPool.map((user, index) => (
                    <Draggable
                      key={user?.id}
                      draggableId={user?.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <MinimalDragItem
                            user={user}
                            index={index}
                            isDragging={snapshot.isDragging}
                            onClick={handleAvatarClick}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Box>
              )}
            </Droppable>
          </CardContent>
        </Card>

        {/* Teams */}
        <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={3}>
          {/* White Team */}
          <Card sx={{ flex: 1, backgroundColor: "#ffffff", border: "2px solid #4a707d" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" color="#4a707d" mb={1}>
                {t("White Team")} ({whiteTeam.length})
              </Typography>
              <Droppable droppableId="white">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      minHeight: 60,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      p: 1,
                      backgroundColor: "#f9f9f9",
                      borderRadius: 2,
                      border: "2px dashed #666",
                    }}
                  >
                    {whiteTeam.map((user, index) => (
                      <Draggable
                        key={user.userStatistics.userName}
                        draggableId={user.userStatistics.userName}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <MinimalDragItem
                              user={user}
                              index={index}
                              isDragging={snapshot.isDragging}
                              onClick={handleAvatarClick}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </CardContent>
          </Card>

          {/* Black Team */}
          <Card sx={{ flex: 1, backgroundColor: "#2c2c2c", color: "white", border: "2px solid #4a707d" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" color="white" mb={1}>
                {t("Black Team")} ({blackTeam.length})
              </Typography>
              <Droppable droppableId="black">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      minHeight: 60,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      p: 1,
                      backgroundColor: "#3c3c3c",
                      borderRadius: 2,
                      border: "2px dashed #ccc",
                    }}
                  >
                    {blackTeam.map((user, index) => (
                      <Draggable
                        key={user.userStatistics.userName}
                        draggableId={user.userStatistics.userName}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <MinimalDragItem
                              user={user}
                              index={index}
                              isDragging={snapshot.isDragging}
                              onClick={handleAvatarClick}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </CardContent>
          </Card>
        </Stack>
      </DragDropContext>

      {/* Team Actions */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center">
            <Fab
              size="small"
              disabled={isTeamEmpty || isSaving}
              onClick={clearTeams}
              color="error"
            >
              <ClearIcon />
            </Fab>

            <input
              type="text"
              placeholder={t("Enter team name")}
              disabled={isTeamEmpty}
              value={customTeamName}
              onChange={(e) => setCustomTeamName(e.target.value)}
              style={{
                flex: 1,
                padding: "10px 15px",
                fontSize: "14px",
                borderRadius: "20px",
                border: "1px solid #ddd",
                outline: "none",
                minWidth: isMobile ? "100%" : "200px",
              }}
            />

            <Fab
              size="small"
              disabled={isTeamEmpty || isSaving}
              onClick={saveTeam}
              color="primary"
              sx={{ backgroundColor: "#4caf50" }}
            >
              <SaveIcon />
            </Fab>
          </Stack>
        </CardContent>
      </Card>

      {/* Saved Teams */}
      <Card>
        <CardContent sx={{ pb: "16px !important" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setSavedTeamsExpanded(!savedTeamsExpanded)}
          >
            <Typography variant="h6" color="#4a707d" fontWeight="bold">
              {t("Saved Teams")} ({savedTeams.length})
            </Typography>
            <IconButton>
              {savedTeamsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={savedTeamsExpanded}>
            <Box sx={{ mt: 2 }}>
              <input
                type="text"
                placeholder={t("Search saved teams...")}
                value={teamSearchTerm}
                onChange={(e) => setTeamSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: "14px",
                  borderRadius: "20px",
                  border: "1px solid #ddd",
                  outline: "none",
                  marginBottom: "16px",
                }}
              />

              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                        {t("Team Name")}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold", fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                        {t("Players")}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold", fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                        {t("Actions")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {savedTeams
                      .filter((team) =>
                        (team.name || "")
                          .toLowerCase()
                          .includes(teamSearchTerm.toLowerCase())
                      )
                      .map((team, idx) => (
                        <TableRow
                          key={idx}
                          hover
                          selected={selectedTeamIndex === idx}
                          onClick={() => handleLoadTeam(idx)}
                          sx={{ cursor: "pointer", backgroundColor: "lightgray" }}
                        >
                          <TableCell sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                            {team.name || `${t("Team")} #${idx + 1}`}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}>
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Chip
                                label={team.whiteTeam.length}
                                size="small"
                                sx={{ backgroundColor: "white", color: "black", fontSize: "0.7rem" }}
                              />
                              <Chip
                                label={team.blackTeam.length}
                                size="small"
                                sx={{ backgroundColor: "black", color: "white", fontSize: "0.7rem" }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePlayTeam(idx);
                                }}
                                size="small"
                                color="primary"
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTeam(idx);
                                }}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TeamBuilder;